import React from 'react';
import { 
    Users, MessageSquare, ShieldCheck, AlertTriangle, 
    TrendingUp, DollarSign, Phone, Activity, Star, Clock 
} from 'lucide-react';
import { MOCK_CLIENTS_FULL } from '../../data/mockData';

// Single Stat Card Component (Unchanged)
const StatCard = ({ label, value, icon: Icon, alertLevel = 'neutral', subtext }) => {
    const colorStyles = {
        neutral: 'bg-slate-800 border-slate-700 text-white',
        warning: 'bg-amber-500/10 border-amber-500/50 text-amber-500',
        danger: 'bg-red-500/10 border-red-500/50 text-red-500',
        success: 'bg-green-500/10 border-green-500/50 text-green-500',
        blue: 'bg-blue-600/10 border-blue-500/50 text-blue-400'
    };

    return (
        <div className={`p-4 rounded-xl border flex items-center justify-between shadow-lg transition-transform hover:scale-[1.02] ${colorStyles[alertLevel]}`}>
            <div>
                <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1">{label}</p>
                <h4 className="text-2xl font-bold">{value}</h4>
                {subtext && <p className="text-[10px] opacity-60 mt-1">{subtext}</p>}
            </div>
            <div className={`p-3 rounded-lg bg-slate-900/30 backdrop-blur-sm`}>
                <Icon size={24} />
            </div>
        </div>
    );
};

// Main Stats Container (Now receiving liveMetrics!)
const DashboardStats = ({ role, liveMetrics }) => {
    
    // --- LIVE DATA ASSIGNMENTS ---
    // If liveMetrics is null (still loading), we show '...' to make it look active
    const totalUsers = liveMetrics?.totalUsers ?? '...';
    const kycApproved = liveMetrics?.kycApproved ?? '...';
    const premiumUsers = liveMetrics?.premiumUsers ?? '...';
    const conversionRate = liveMetrics?.kycConversionRate ?? '...';

    // --- MOCK FALLBACKS FOR MISSING ENDPOINTS ---
    // We keep this for Tickets/Calls since the API doesn't have them yet
    const openTicketsCount = MOCK_CLIENTS_FULL.reduce((acc, client) => {
        return acc + (client.tickets?.filter(t => t.status === 'Open' || t.status === 'New').length || 0);
    }, 0);

    // --- 1. HYBRID AGENT CONFIGURATION ---
    if (role?.includes('Hybrid')) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-in-down">
                <StatCard 
                    label="Total Live Users" 
                    value={totalUsers} 
                    icon={Users} 
                    alertLevel="blue" 
                    subtext="Synced from Nommia DB"
                />
                <StatCard 
                    label="KYC Approved" 
                    value={kycApproved} 
                    icon={ShieldCheck} 
                    alertLevel="success" 
                    subtext={`${conversionRate}% Conversion Rate`}
                />
                <StatCard 
                    label="Premium Members" 
                    value={premiumUsers} 
                    icon={Star} 
                    alertLevel="warning" 
                    subtext="Pro & Elite Tiers"
                />
                <StatCard 
                    label="Open Tickets" 
                    value={openTicketsCount} 
                    icon={MessageSquare} 
                    alertLevel={openTicketsCount > 5 ? "warning" : "neutral"} 
                    subtext="Total active support cases"
                />
            </div>
        );
    }

    // --- 2. SALES MANAGER CONFIGURATION ---
    if (role?.includes('Sales')) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-in-down">
                <StatCard 
                    label="Total Platform Users" 
                    value={totalUsers} 
                    icon={Users} 
                    alertLevel="blue" 
                    subtext="Total registered leads"
                />
                <StatCard 
                    label="Premium Members" 
                    value={premiumUsers} 
                    icon={Star} 
                    alertLevel="warning" 
                    subtext="Pro & Elite Tiers"
                />
                <StatCard 
                    label="KYC Conversion" 
                    value={`${conversionRate}%`} 
                    icon={TrendingUp} 
                    alertLevel="neutral" 
                    subtext={`${kycApproved} fully verified`}
                />
                <StatCard 
                    label="Staff Online" 
                    value="8/10" 
                    icon={Activity} 
                    alertLevel="success" 
                    subtext="Live on Floor"
                />
            </div>
        );
    }

    // --- 3. SUPPORT MANAGER CONFIGURATION ---
    if (role?.includes('Support')) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-in-down">
                <StatCard 
                    label="Total Users" 
                    value={totalUsers} 
                    icon={Users} 
                    alertLevel="blue" 
                    subtext="Supported Client Base"
                />
                <StatCard 
                    label="Queue Volume" 
                    value={openTicketsCount} 
                    icon={MessageSquare} 
                    alertLevel="warning" 
                    subtext="Total open tickets"
                />
                <StatCard 
                    label="Avg Response" 
                    value="4m 12s" 
                    icon={Clock} 
                    alertLevel="neutral" 
                    subtext="Target: < 5m"
                />
                <StatCard 
                    label="Daily CSAT" 
                    value="4.8/5" 
                    icon={Star} 
                    alertLevel="success" 
                    subtext="Based on 15 reviews"
                />
            </div>
        );
    }

    // --- 4. DEFAULT FALLBACK ---
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-in-down">
            <StatCard label="Total Clients" value={totalUsers} icon={Users} alertLevel="blue" subtext="Synced live" />
            <StatCard label="KYC Approved" value={kycApproved} icon={ShieldCheck} alertLevel="success" subtext={`${conversionRate}% Conversion`} />
            <StatCard label="Talk Time" value="2h 15m" icon={Activity} alertLevel="neutral" subtext="Daily Progress" />
            <StatCard label="Premium Members" value={premiumUsers} icon={Star} alertLevel="warning" subtext="Pro & Elite Tiers" />
        </div>
    );
};

export default DashboardStats;